import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Archive } from 'lucide-react';
import { useMemo } from 'react';
import { EmptyState, PageHeader, Table, type TableColumn } from '~/components/data';
import { fetchCashFlowSnapshots, fetchCogsSnapshots, fetchPnlSnapshots } from '~/lib/api/finance';
import { formatDate } from '~/lib/format';
import styles from './_auth.finance.snapshots.module.css';

/**
 * S15-FE.4 — Snapshots browser. Три секции: COGS / Cash-flow / P&L.
 */

export const Route = createFileRoute('/_auth/finance/snapshots')({ component: SnapshotsPage });

type Row = {
  id: string;
  period: { date_from: string; date_to: string };
  created_at: string;
};

function SnapshotSection({
  title,
  rows,
  loading,
}: {
  title: string;
  rows: Row[];
  loading: boolean;
}): JSX.Element {
  const columns: TableColumn<Row>[] = useMemo(
    () => [
      {
        id: 'id',
        header: m.esf_column_doc_number(),
        accessor: (r) => r.id.slice(0, 8),
        mono: true,
        sticky: true,
      },
      {
        id: 'period',
        header: m.esf_column_period(),
        accessor: (r) => `${formatDate(r.period.date_from)} — ${formatDate(r.period.date_to)}`,
      },
      {
        id: 'created',
        header: m.invoice_column_issue_date(),
        accessor: (r) => formatDate(r.created_at.slice(0, 10)),
        width: '140px',
      },
    ],
    [],
  );
  return (
    <section className={styles.section}>
      <h2 className={styles.heading}>{title}</h2>
      {rows.length === 0 && !loading ? (
        <EmptyState title={m.cogs_empty_title()} icon={<Archive size={28} />} />
      ) : (
        <Table<Row>
          data={rows}
          columns={columns}
          rowKey={(r) => r.id}
          ariaLabel={title}
          loading={loading}
          emptyTitle={m.cogs_empty_title()}
        />
      )}
    </section>
  );
}

function SnapshotsPage(): JSX.Element {
  const cogs = useQuery({ queryKey: ['finance', 'cogs-snapshots'], queryFn: fetchCogsSnapshots });
  const cf = useQuery({
    queryKey: ['finance', 'cf-snapshots'],
    queryFn: fetchCashFlowSnapshots,
  });
  const pnl = useQuery({ queryKey: ['finance', 'pnl-snapshots'], queryFn: fetchPnlSnapshots });

  return (
    <div>
      <PageHeader title="Snapshots" />
      <SnapshotSection
        title={m.phase2_finance_cogs_title()}
        rows={cogs.data?.items ?? []}
        loading={cogs.isLoading}
      />
      <SnapshotSection
        title={m.phase2_finance_cash_flow_title()}
        rows={cf.data?.items ?? []}
        loading={cf.isLoading}
      />
      <SnapshotSection
        title={m.phase2_finance_pnl_title()}
        rows={pnl.data?.items ?? []}
        loading={pnl.isLoading}
      />
    </div>
  );
}
